import {LockIcon, MediaPackagePhotoIcon, MediaPackageVideoIcon, MicromorgiIcon} from '@/assets/icons/icons'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Button} from '@/components/ui/button/Button'
import {MediaLightBox} from '@/components/ui/media-light-box/MediaLightBox'
import {getLocalDate} from '@/dayjs'
import {HostTypeV3} from '@/features/authentication/types'
import {useUnlockMediaPackage} from '@/features/mediaPackages/queries/useUnlockMediaPackage'
import {HostInfo} from '@/features/my-moments/components/moment-item/host-info/HostInfo'
import {
    StyledContentWrapper,
    StyledDate,
    StyledFullNameContainer,
    StyledHost,
    StyledInfoWrapper,
    StyledWrapper
} from '@/features/my-moments/components/moment-item/style'
import {MURAL_TYPES} from '@/features/profile/services/completeProfile.schema'
import useWindowDimensions from '@/hooks/useWindowDimensions'
import {useRootStore} from '@/store'
import {selectBuyMorgisModal, selectRecurringGiftModal} from '@/store/selectors'
import {checkMediaExtension} from '@/utilities/helpers'
import {FC, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useTheme} from 'styled-components'
import {StyledCounterItem, StyledCounterItemsContainer, StyledPackageName, StyledUnpaidContentWrapper} from './style'
import HostProfileMuralPreview from '../host-profile-mural-preview/HostProfileMuralPreview'
import {UPDATE_VARIANT} from '@/features/feed/queries/useGetContentById'
import {HostFeedCardV3} from '@/features/feed/types'

type HostProfileMuralItemProps = {
    post: HostFeedCardV3
    host: Partial<HostTypeV3>
    defaultItem?: {url: string; muralType: MURAL_TYPES}
    guestsBalance?: number
}
const HostProfileMuralItem: FC<HostProfileMuralItemProps> = ({post, host, defaultItem, guestsBalance}) => {
    const {openModal: openGiftModal} = useRootStore(selectRecurringGiftModal)
    const {openModal: openModalToBuyMicromorgi} = useRootStore(selectBuyMorgisModal)
    const {t} = useTranslation()
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const {muralType, type, createdAt, url, blurredUrl, mediaPackagesContents, blurHash, id, isAdultContent} = post
    const isMediaPackage = muralType === MURAL_TYPES.MEDIA_PACKAGES
    const formattedDate = getLocalDate(createdAt, 'MMM D, YYYY')
    const openSubscribeModal = () => {
        openGiftModal({hostID: String(host.id)})
    }
    const {breakpoints} = useTheme()
    const {width} = useWindowDimensions()
    const [isMobile, setIsMobile] = useState(width <= breakpoints.width.m)
    const {mutate: unlockMediaPackage} = useUnlockMediaPackage()
    const [currentMediaPackageIndex, setCurrentMediaPackageIndex] = useState(0)
    const isOpenContent =
        isMediaPackage && mediaPackagesContents ? !!mediaPackagesContents[currentMediaPackageIndex]?.url : !!url
    const isPaid = isMediaPackage
        ? post.isMediaPackageBought
        : Boolean((isAdultContent && (blurHash || post.url)) || (!isAdultContent && isOpenContent))

    const isBlurred =
        !isOpenContent &&
        Boolean(isMediaPackage ? post.mediaPackagesContents?.[currentMediaPackageIndex].blurHash : post.blurHash)

    const onChangeCurrentMediaPackageIndex = (currentIndex: number) => {
        if (isMediaPackage) setCurrentMediaPackageIndex(currentIndex)
    }
    useEffect(() => {
        setIsMobile(width <= breakpoints.width.m)
    }, [width])
    const onClickUnlockMediaPackage = () => {
        if (typeof guestsBalance === 'number' && !!post.amount) {
            if (post.amount > guestsBalance) openModalToBuyMicromorgi({})
            else unlockMediaPackage({creatorId: String(host.id), mediaPackageId: String(post.id)})
        }
    }
    const mediaMap = [
        ...new Set(
            (isMediaPackage && mediaPackagesContents
                ? mediaPackagesContents.map(content => content.url || content.blurredUrl)
                : [url ?? blurredUrl]
            ).map(m => {
                const mediaWithoutExtraPayload = checkMediaExtension(m ?? '')
                if (mediaWithoutExtraPayload) {
                    return {
                        ...mediaWithoutExtraPayload,
                        extraPayload: {
                            url: m,
                            optimizedUrl: m,
                            optimizedUrls: [m]
                        }
                    }
                }
                return undefined
            })
        )
    ]

    const UNPAID_CONTENT = {
        [MURAL_TYPES.MEDIA_PACKAGES]: () => {
            const imagesCounter = post.mediaPackagesContentsCounts?.image || 0
            const videosCounter = post.mediaPackagesContentsCounts?.video || 0
            return (
                <StyledUnpaidContentWrapper>
                    <LockIcon width={isMobile ? 105 : 180} height={isMobile ? 105 : 180} />
                    <StyledPackageName>{post.name}</StyledPackageName>
                    <StyledCounterItemsContainer>
                        {imagesCounter > 0 && (
                            <StyledCounterItem>
                                {imagesCounter}
                                <MediaPackagePhotoIcon />
                            </StyledCounterItem>
                        )}
                        {videosCounter > 0 && (
                            <StyledCounterItem>
                                {videosCounter}
                                <MediaPackageVideoIcon />
                            </StyledCounterItem>
                        )}
                    </StyledCounterItemsContainer>
                    <StyledCounterItemsContainer>
                        <MicromorgiIcon height={isMobile ? 20 : 32} width={isMobile ? 20 : 32} />
                        {post.amount}
                    </StyledCounterItemsContainer>
                </StyledUnpaidContentWrapper>
            )
        },
        [MURAL_TYPES.PHOTOS]: () => (
            <StyledUnpaidContentWrapper>
                <LockIcon />
                <Button onClick={openSubscribeModal}>
                    {t('pages:rookie_profile_page:subscribe_for_content', {NAME: host.fullName})}
                </Button>
            </StyledUnpaidContentWrapper>
        ),
        [MURAL_TYPES.VIDEOS]: () => (
            <StyledUnpaidContentWrapper>
                <LockIcon />
                <Button onClick={openSubscribeModal}>
                    {t('pages:rookie_profile_page:subscribe_for_content', {NAME: host.fullName})}
                </Button>
            </StyledUnpaidContentWrapper>
        )
    }[muralType as Exclude<MURAL_TYPES, MURAL_TYPES.CHAT>]

    return (
        <>
            <StyledWrapper>
                <StyledInfoWrapper isMediaPackage={!!mediaPackagesContents}>
                    <StyledHost>
                        <StyledFullNameContainer>
                            <Avatar
                                imageUrl={host.avatar?.url}
                                width={54}
                                height={54}
                                name={host.fullName}
                                initialFontSize={12}
                            />
                            <div>
                                <p>{host.fullName}</p>
                                <StyledDate>{formattedDate}</StyledDate>
                            </div>
                        </StyledFullNameContainer>
                    </StyledHost>

                    <StyledContentWrapper onClick={isOpenContent ? () => setIsPreviewOpen(true) : undefined}>
                        <HostProfileMuralPreview
                            post={post}
                            muralType={muralType}
                            type={type}
                            url={
                                isMediaPackage
                                    ? post.mediaPackagesContents?.[0].url || post.mediaPackagesContents?.[0]?.blurHash
                                    : post.url ?? post?.blurHash
                            }
                            isPaid={isPaid}
                            isBlurred={isBlurred}
                            extraUnpaidContent={UNPAID_CONTENT}
                            defaultItem={defaultItem}
                            mediaPackagesContents={isMediaPackage ? mediaPackagesContents : undefined}
                            onChangeCurrentMediaPackageIndex={
                                isMediaPackage ? onChangeCurrentMediaPackageIndex : undefined
                            }
                            isAdultContent={isAdultContent}
                            contentId={id.toString()}
                            variant={UPDATE_VARIANT.PROFILE_MURAL}
                        />
                    </StyledContentWrapper>
                    {!isPaid && muralType === MURAL_TYPES.MEDIA_PACKAGES && (
                        <Button onClick={onClickUnlockMediaPackage}>
                            {t('pages:rookie_profile_page:unlock_media_package')}
                        </Button>
                    )}
                </StyledInfoWrapper>
            </StyledWrapper>

            {isPreviewOpen && (
                <MediaLightBox
                    currentIndex={isMediaPackage ? currentMediaPackageIndex : undefined}
                    media={mediaMap}
                    onClose={() => setIsPreviewOpen(false)}
                    headerInfo={
                        <HostInfo
                            date={formattedDate}
                            hostName={host?.fullName}
                            hostAvatar={host?.avatar?.url}
                            isSurprise={false}
                        />
                    }
                />
            )}
        </>
    )
}
export default HostProfileMuralItem
